import { useEffect, useState } from 'preact/hooks';
import { motion } from 'framer-motion';
import { ACCENT_GOLD, GOLD, TEXT_MUTED } from '../../lib/medievalTheme';
import { getLocalApiOrigin } from '../../utils/localApi';
import { sorceryImageUrl } from '../../utils/cardImageUrl';

// Booster art lives behind the local server's /game-assets route, not
// /public. Mirror the store's resolver so asset URLs match whatever
// the packaged app / dev server actually serves.
function getBoosterImage(setKey) {
  return `${getLocalApiOrigin()}/game-assets/booster-${setKey}.webp`;
}

// Per-pack scale. Arthurian art sits small in its canvas and needs
// a big boost to read at the same visual weight as the other packs.
// Numbers intentionally match ArenaStore.BOOSTER_SCALE so the three
// packs look like a matched set in either place.
const BOOSTER_SCALE = {
  gothic: 1,
  arthurian: 1.85,
  beta: 1,
};

/**
 * Art components for the Play-screen mode tiles. Each one renders a
 * full-bleed backdrop inside the 16:9 tile. The ModeTile frame draws
 * its own border, gradient overlay, and corner plating on top, so
 * these components only need to fill the box with the mode's
 * thematic imagery.
 *
 * Kept as pure UI (icons + styled divs. No raster heroes) so we can
 * iterate on them without waiting on art, and so they scale crisply
 * at every tile size.
 */

const BG_DARK = 'linear-gradient(135deg, #14100a 0%, #0b0806 60%, #07050a 100%)';

function OrnateBackdrop({ accentColor = ACCENT_GOLD, children }) {
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: BG_DARK }}>
      {/* Soft radial wash so the centerpiece reads as lit */}
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(ellipse at 50% 45%, ${GOLD} 0.14) 0%, transparent 60%)`,
          mixBlendMode: 'screen',
        }}
      />
      {/* Subtle rune-ish radial lines from the center */}
      <div
        className="absolute inset-0"
        style={{
          background: `repeating-conic-gradient(from 0deg at 50% 50%, rgba(212,168,67,0.06) 0deg, transparent 3deg 15deg)`,
          opacity: 0.5,
        }}
      />
      {/* Vignette around the edges */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, transparent 55%, rgba(0,0,0,0.75) 100%)',
        }}
      />
      <div className="absolute inset-0" style={{ filter: `drop-shadow(0 2px 12px ${accentColor}55)` }}>
        {children}
      </div>
    </div>
  );
}

// Row-level animation variants driven by whether THIS row is the one
// currently wearing the highlight. The highlighted row gets the gold
// border + warm gradient + glow; non-highlighted rows are muted. Both
// transitions use the same spring so the baton-pass from row N to
// row N-1 reads as one continuous climb rather than two snaps.
const rankedRowStateVariants = {
  muted: {
    background: 'rgba(20,16,10,0.7)',
    borderColor: 'rgba(180,140,60,0.22)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.45)',
    scale: 1,
    transition: { type: 'spring', stiffness: 220, damping: 26 },
  },
  highlighted: {
    background: 'linear-gradient(90deg, rgba(212,168,67,0.28) 0%, rgba(212,168,67,0.08) 100%)',
    borderColor: ACCENT_GOLD,
    boxShadow: '0 4px 22px rgba(212,168,67,0.55), inset 0 1px 0 rgba(255,220,140,0.3)',
    scale: 1.02,
    transition: { type: 'spring', stiffness: 240, damping: 22 },
  },
};

const rankedBadgeStateVariants = {
  muted: {
    background: 'rgba(180,140,60,0.12)',
    borderColor: 'rgba(180,140,60,0.3)',
    color: ACCENT_GOLD,
    scale: 1,
    transition: { type: 'spring', stiffness: 220, damping: 26 },
  },
  highlighted: {
    // Radial gradient can't be animated, so we use a flat warm gold
    // that visually reads as the same lit badge.
    background: '#f6d172',
    borderColor: 'rgba(255,220,140,0.7)',
    color: '#1a1208',
    scale: 1.05,
    transition: { type: 'spring', stiffness: 240, damping: 22 },
  },
};

const rankedNameBarStateVariants = {
  muted: {
    background: 'linear-gradient(90deg, rgba(180,140,60,0.35) 0%, rgba(180,140,60,0.1) 100%)',
    transition: { duration: 0.25 },
  },
  highlighted: {
    background: `linear-gradient(90deg, ${ACCENT_GOLD} 0%, rgba(212,168,67,0.4) 100%)`,
    transition: { duration: 0.25 },
  },
};

// How long each intermediate step of the hover/un-hover climb takes.
// Short enough that the full 3 → 2 → 1 journey feels like one fluid
// animation rather than three beats.
const HIGHLIGHT_STEP_MS = 220;

export function RankedHero({ hovered }) {
  // Leaderboard climb: each row is a distinct "player" with a fixed
  // identity (stable key, fixed name / XP bar widths). Rank numbers
  // 1-5 are derived from DISPLAY POSITION. Top row is always #1,
  // bottom is always #5. The "you" row starts at position #3; on
  // hover we swap it with the row above, one step at a time, until
  // it reaches #1. On un-hover it swaps back down to #3.
  //
  // framer-motion's `layout` prop handles the slide animation
  // automatically. We just reorder the array and each row glides
  // to its new y position without us computing offsets manually.
  //
  // Row widths are baked in per identity so a swapped row's name
  // bar doesn't magically change size when it moves. It's the
  // same player moving, not a different one taking its place.
  const INITIAL_ORDER = [
    { id: 'p1', nameW: '68%', xpW: '24%' },
    { id: 'p2', nameW: '55%', xpW: '20%' },
    { id: 'me', nameW: '62%', xpW: '22%' },
    { id: 'p4', nameW: '50%', xpW: '18%' },
    { id: 'p5', nameW: '58%', xpW: '20%' },
  ];
  const PLAYER_ID = 'me';
  const BASELINE_INDEX = 2; // zero-indexed → displayed rank 3
  const TOP_INDEX = 0;      // zero-indexed → displayed rank 1

  const [order, setOrder] = useState(INITIAL_ORDER);

  useEffect(() => {
    // Find where "you" currently sit in the order, then step one
    // swap at a time toward the target index. Each step swaps the
    // player row with the one immediately above (or below, on
    // un-hover) via a fresh array so framer-motion sees the layout
    // change and animates the slide.
    const timers = [];
    const currentIndex = order.findIndex((r) => r.id === PLAYER_ID);
    const targetIndex = hovered ? TOP_INDEX : BASELINE_INDEX;
    const direction = targetIndex < currentIndex ? -1 : targetIndex > currentIndex ? 1 : 0;
    if (direction === 0) return undefined;

    // Snapshot the order at effect-start so the timer chain walks
    // from the CURRENT state rather than recomputing against a
    // stale closure on each step.
    let working = order.slice();
    let playerAt = currentIndex;
    let step = 0;

    while (playerAt !== targetIndex) {
      const from = playerAt;
      const to = playerAt + direction;
      step += 1;
      const snapshot = working.slice();
      [snapshot[from], snapshot[to]] = [snapshot[to], snapshot[from]];
      working = snapshot;
      const stepOrder = snapshot;
      timers.push(setTimeout(() => setOrder(stepOrder), step * HIGHLIGHT_STEP_MS));
      playerAt = to;
    }
    return () => timers.forEach((t) => clearTimeout(t));
  }, [hovered]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <OrnateBackdrop>
      <div
        className="absolute left-1/2 bottom-0"
        style={{
          width: '76%',
          transform: 'translate(-50%, 12%)',
        }}
      >
        {order.map((r, positionIdx) => {
          const isPlayer = r.id === PLAYER_ID;
          const rowState = isPlayer ? 'highlighted' : 'muted';
          const displayRank = positionIdx + 1;
          return (
            <motion.div
              key={r.id}
              layout
              transition={{ type: 'spring', stiffness: 260, damping: 26 }}
              className="flex items-center gap-3"
              style={{
                padding: '7px 10px',
                marginBottom: 6,
                borderStyle: 'solid',
                borderWidth: 1,
                borderRadius: 'var(--radius-md)',
                transformOrigin: 'center center',
                willChange: 'transform, background, border-color, box-shadow',
              }}
              animate={rowState}
              initial={rowState}
              variants={rankedRowStateVariants}
            >
              <motion.div
                style={{
                  width: '1.375cqw',
                  height: '1.375cqw',
                  borderRadius: '50%',
                  borderStyle: 'solid',
                  borderWidth: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.625cqw',
                  fontWeight: 800,
                  fontFamily: "'Geist Variable', sans-serif",
                  flexShrink: 0,
                }}
                animate={rowState}
                initial={rowState}
                variants={rankedBadgeStateVariants}
              >
                {displayRank}
              </motion.div>
              <motion.div
                style={{
                  height: '0.5cqw',
                  width: r.nameW,
                  borderRadius: 'var(--radius-xs)',
                }}
                animate={rowState}
                initial={rowState}
                variants={rankedNameBarStateVariants}
              />
              <div
                style={{
                  height: '0.5cqw',
                  width: r.xpW,
                  marginLeft: 'auto',
                  borderRadius: 'var(--radius-xs)',
                  background: `${GOLD} 0.18)`,
                }}
              />
            </motion.div>
          );
        })}
      </div>
    </OrnateBackdrop>
  );
}

// Fighting-game VS hover variants: on hover the two halves push
// inward toward the crease (the "just before the bell" squeeze),
// VS pulses up with a subtle tilt, and the lightning brightens.
// Everything springs back cleanly on un-hover.
const FM_LEFT_VARIANTS = {
  idle: { x: 0, scale: 1, transition: { type: 'spring', stiffness: 240, damping: 22 } },
  hover: { x: '-2%', scale: 1.02, transition: { type: 'spring', stiffness: 260, damping: 20 } },
};
const FM_RIGHT_VARIANTS = {
  idle: { x: 0, scale: 1, transition: { type: 'spring', stiffness: 240, damping: 22 } },
  hover: { x: '2%', scale: 1.02, transition: { type: 'spring', stiffness: 260, damping: 20 } },
};
const FM_VS_VARIANTS = {
  idle: {
    scale: 1,
    rotate: 0,
    textShadow: '0 4px 14px rgba(0,0,0,0.9), 0 0 26px rgba(255,220,140,0.55)',
    transition: { type: 'spring', stiffness: 200, damping: 14 },
  },
  hover: {
    scale: [1, 1.22, 1.14],
    rotate: [0, -3, 0],
    textShadow: '0 4px 18px rgba(0,0,0,0.95), 0 0 44px rgba(255,220,140,0.9)',
    transition: {
      duration: 0.55,
      times: [0, 0.45, 1],
      ease: 'easeOut',
    },
  },
};
const FM_LIGHTNING_VARIANTS = {
  idle: { opacity: 0.85, transition: { duration: 0.25 } },
  hover: { opacity: 1, transition: { duration: 0.2 } },
};

export function FriendMatchHero({ hovered }) {
  // Fighting-game style VS splash: Harbinger on the left, Necromancer
  // on the right, with a jagged lightning-bolt crease running diagonally
  // between them and a bold "VS" anchored on that crease. The two
  // avatar halves are clipped with angled polygons so their edges
  // interlock along the lightning rather than sitting flat.
  //
  // Avatar artwork is pulled straight from the bundled Sorcery image
  // cache (same path normalizeCards.js builds URLs against), so the
  // portraits match what you'd see anywhere else in the app.
  const HARBINGER = sorceryImageUrl('got-harbinger-b-s');
  const NECROMANCER = sorceryImageUrl('got-necromancer-b-s');

  // Lightning crease polygon that cuts between the two portraits.
  // Uses percentage points. The zigzag shifts left/right a few
  // percent at each vertical stop to read as a jagged fracture
  // rather than a straight diagonal.
  const leftClip = 'polygon(0 0, 56% 0, 46% 22%, 56% 44%, 42% 60%, 54% 82%, 44% 100%, 0 100%)';
  const rightClip = 'polygon(56% 0, 100% 0, 100% 100%, 44% 100%, 54% 82%, 42% 60%, 56% 44%, 46% 22%)';

  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: BG_DARK }}>
      {/* Left half. Harbinger. We drop `object-cover` here and pin
          the image by explicit size + transform: the card art is
          portrait with the character's face in the upper portion,
          and `object-cover` in a 16:9 tile keeps the image centered
          vertically. Which drops the face off the top. Sizing to
          `height: 170%` with a `translate(-50%, -30%)` yank slides
          the face roughly into the left-half centerpoint regardless
          of the clip, while the horizontal `left: 28%` seats the
          character inside the visible (pre-clip) left half instead
          of at the tile's midpoint. */}
      <motion.div
        className="absolute inset-0"
        style={{ clipPath: leftClip, WebkitClipPath: leftClip, willChange: 'transform' }}
        initial="idle"
        animate={hovered ? 'hover' : 'idle'}
        variants={FM_LEFT_VARIANTS}
      >
        <img
          src={HARBINGER}
          alt=""
          draggable={false}
          className="max-w-none"
          style={{
            position: 'absolute',
            left: '28%',
            top: '50%',
            height: '170%',
            width: 'auto',
            transform: 'translate(-50%, -30%)',
          }}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        {/* Warm red wash to colour-code the left side */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, rgba(168,32,32,0.55) 0%, rgba(40,12,12,0.3) 60%, transparent 100%)',
            mixBlendMode: 'multiply',
          }}
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(90deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.7) 100%)' }}
        />
      </motion.div>

      {/* Right half. Necromancer */}
      <motion.div
        className="absolute inset-0"
        style={{ clipPath: rightClip, WebkitClipPath: rightClip, willChange: 'transform' }}
        initial="idle"
        animate={hovered ? 'hover' : 'idle'}
        variants={FM_RIGHT_VARIANTS}
      >
        <img
          src={NECROMANCER}
          alt=""
          draggable={false}
          className="max-w-none"
          style={{
            position: 'absolute',
            left: '72%',
            top: '50%',
            height: '170%',
            width: 'auto',
            transform: 'translate(-50%, -30%)',
          }}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        {/* Cool blue wash for the right side */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(225deg, rgba(40,72,168,0.5) 0%, rgba(12,20,40,0.3) 60%, transparent 100%)',
            mixBlendMode: 'multiply',
          }}
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(-90deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.7) 100%)' }}
        />
      </motion.div>

      {/* Lightning crease. SVG polyline traced along the same vertex
          set the clip paths use, so the glow sits precisely between
          the two halves. Doubled up: a fat outer blur for the glow,
          a thin inner stroke for the sharp gold edge. */}
      <motion.svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden
        initial="idle"
        animate={hovered ? 'hover' : 'idle'}
        variants={FM_LIGHTNING_VARIANTS}
      >
        <defs>
          <filter id="lightning-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <polyline
          points="56,0 46,22 56,44 42,60 54,82 44,100"
          fill="none"
          stroke="rgba(255, 240, 180, 0.45)"
          strokeWidth="2.8"
          strokeLinecap="round"
          strokeLinejoin="miter"
          vectorEffect="non-scaling-stroke"
          filter="url(#lightning-glow)"
        />
        <polyline
          points="56,0 46,22 56,44 42,60 54,82 44,100"
          fill="none"
          stroke={ACCENT_GOLD}
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="miter"
          vectorEffect="non-scaling-stroke"
        />
      </motion.svg>

      {/* Ambient vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 50% 50%, transparent 50%, rgba(0,0,0,0.8) 100%)' }}
      />

      {/* VS label. Anchored at the dead-centre of the tile, which is
          where the lightning crease crosses (interpolating the
          56→46→56→42→54→44 polyline, y=50% lands almost exactly at
          x=50%). Sitting this here reads as the focal point of both
          the portraits and the fracture between them. */}
      <div
        className="absolute"
        style={{
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
        }}
      >
        <motion.span
          className="arena-heading font-bold inline-block"
          style={{
            color: '#fff6d5',
            fontSize: '3.375cqw',
            letterSpacing: '0.04em',
            fontStyle: 'italic',
            WebkitTextStroke: `0.125cqw ${ACCENT_GOLD}`,
            transformOrigin: 'center center',
            willChange: 'transform',
          }}
          initial="idle"
          animate={hovered ? 'hover' : 'idle'}
          variants={FM_VS_VARIANTS}
        >
          VS
        </motion.span>
      </div>
    </div>
  );
}

// Hover orchestrator for the Solo hand. Staggers from the middle
// outward. Center card fans first, then its neighbours, then the
// far wings. So the hand reads as "blossoming" when you hover.
const SOLO_HAND_VARIANTS = {
  idle: { transition: { staggerChildren: 0.04, staggerDirection: 1 } },
  hover: { transition: { staggerChildren: 0.05, staggerDirection: 1 } },
};

// Build variants per-card so we keep the card's own idle pose (rot /
// x / y from the hand fan layout) as the base and only multiply it
// on hover. Index distance from the middle drives how much extra
// lift a card gets. The centerpiece rises highest, outer cards
// spread further out. All translations are in cqw so the fan scales
// with the canvas instead of staying frozen at a raw px offset.
function soloCardVariants(card, i, centerIdx) {
  const distFromCenter = Math.abs(i - centerIdx);
  const liftIdle = 0;
  // Middle card rises most; wings rise less but spread more. Was
  // `-(16 - dist*3)` px; now `-(1 - dist*0.1875)` cqw.
  const liftHover = -(1 - distFromCenter * 0.1875);
  const rotMul = 1.6;
  const xMul = 1.3;
  return {
    idle: {
      x: `${card.x}cqw`,
      y: `${card.y + liftIdle}cqw`,
      rotate: card.rot,
      transition: { type: 'spring', stiffness: 240, damping: 24 },
    },
    hover: {
      x: `${card.x * xMul}cqw`,
      y: `${card.y + liftHover}cqw`,
      rotate: card.rot * rotMul,
      transition: { type: 'spring', stiffness: 260, damping: 20 },
    },
  };
}

export function SoloHero({ hovered }) {
  // Fanned hand of card-back silhouettes at the bottom, like a
  // player's on-table hand. Uses the same warm-gold → deep-brown
  // gradient the real spellbook card back uses so it reads as a
  // Sorcery hand, not a generic card motif. Card offsets are in cqw
  // (old px ÷ 16) so the fan grows with the canvas.
  const cards = [
    { rot: -18, x: -6.875, y: 1.125 },
    { rot: -9,  x: -3.4375, y: 0.375 },
    { rot: 0,   x: 0, y: 0 },
    { rot: 9,   x: 3.4375, y: 0.375 },
    { rot: 18,  x: 6.875, y: 1.125 },
  ];
  const centerIdx = Math.floor(cards.length / 2);
  const state = hovered ? 'hover' : 'idle';
  return (
    <OrnateBackdrop>
      <div className="absolute inset-0 flex items-end justify-center" style={{ paddingBottom: '6%' }}>
        <motion.div
          style={{ position: 'relative', width: 1, height: 1 }}
          initial="idle"
          animate={state}
          variants={SOLO_HAND_VARIANTS}
        >
          {cards.map((c, i) => (
            <motion.div
              key={i}
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: '4.625cqw',
                height: '6.5cqw',
                marginLeft: '-2.3125cqw',  // centre the card horizontally on its transform origin
                marginTop: '-6.5cqw',      // anchor the bottom edge of the card at (0,0)
                transformOrigin: 'center bottom',
                background: 'linear-gradient(145deg, #3a2710 0%, #2a1a08 55%, #1a1106 100%)',
                borderRadius: 'var(--radius-md)',
                border: `1.5px solid ${ACCENT_GOLD}`,
                boxShadow: '0 6px 14px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,220,140,0.2)',
                willChange: 'transform',
              }}
              variants={soloCardVariants(c, i, centerIdx)}
            >
              {/* Blank inner panel. No glyph. Keeps the card backs
                  reading as a stack of plain spellbook cards. */}
              <div
                className="absolute inset-2"
                style={{
                  border: `1px solid ${GOLD} 0.35)`,
                  borderRadius: 'var(--radius-sm)',
                }}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </OrnateBackdrop>
  );
}

// ---------------------------------------------------------------------------
// Sandbox hero. A scattered cluster of real card art that drifts gently
// in place, then sweeps leftward in a unified gust when the user hovers.
// Visual thesis: Sandbox is the creative workshop where any card is on
// the table. Showing actual card faces. Varied, overlapping, chaotic.
// reads as "every card in the game is fair game here" instead of the
// clean, deliberate fans the ranked / draft / sealed tiles use.
// ---------------------------------------------------------------------------

// Layout is expressed in cqw (1cqw = 16 design-px on a 1600 canvas) so
// the scatter scales with the canvas instead of staying frozen at a
// raw px size. Values below are the old px constants divided by 16.
const SANDBOX_AREA = { W: 32.5, H: 14.375 };  // virtual canvas (was 520×230 px)
const SANDBOX_CARD = { W: 4.25, H: 6 };       // per-card footprint (was 68×96 px)

// Deterministic "brick" grid: three rows, with the middle row offset
// by half a column so cards interlock rather than stacking directly on
// top of each other. Each slot is hand-placed so the composition is
// stable and guaranteed-readable; only per-card rotation and bob
// parameters are random.
const SANDBOX_SLOTS = [
  // Top row (3 cards, slight back-depth)
  { gx: -12.5,  gy: -4.875, depth: 0.82 },
  { gx:   0,    gy: -5.375, depth: 0.78 },
  { gx:  12.5,  gy: -4.875, depth: 0.82 },
  // Middle row. Staggered half-a-column between top/bottom. Full-size
  // foreground tier; these are the "headliners" the eye reads first.
  { gx:  -8,    gy:  0,     depth: 1.00 },
  { gx:  -2,    gy:  0.625, depth: 1.00 },
  { gx:   5,    gy:  0,     depth: 1.00 },
  { gx:  12.5,  gy:  0.875, depth: 0.92 },
  // Bottom row (2 cards, mirror of top, mid-depth)
  { gx: -12.5,  gy:  4.875, depth: 0.86 },
  { gx:   0,    gy:  5.375, depth: 0.82 },
];
const SANDBOX_CARD_COUNT = SANDBOX_SLOTS.length;

let _sandboxSampleCache = { source: null, picks: null, layout: null };

function pickSandboxCards(sorceryCards) {
  if (_sandboxSampleCache.source === sorceryCards && _sandboxSampleCache.picks) {
    return _sandboxSampleCache;
  }
  const candidates = Array.isArray(sorceryCards) ? sorceryCards.filter((c) => {
    const types = Array.isArray(c?.types) ? c.types : [];
    if (types.includes('Avatar')) return false;
    return !!c?.printings?.[0]?.image_url;
  }) : [];
  if (candidates.length === 0) return { picks: [], layout: [] };

  const n = Math.min(SANDBOX_CARD_COUNT, candidates.length);
  const pool = candidates.slice();
  for (let i = 0; i < n; i += 1) {
    const j = i + Math.floor(Math.random() * (pool.length - i));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  const picks = pool.slice(0, n);

  // Layout. Slots give us stable positions; we add tiny jitter on top
  // (±6 px) so the composition feels organic, not gridded. `outDir`
  // is a unit vector away from the cluster centre, used on hover to
  // push each card radially outward (fan-out reveal).
  const layout = picks.map((_, i) => {
    const slot = SANDBOX_SLOTS[i];
    // Jitter in cqw. Was ±6px / ±4px, now ±0.375cqw / ±0.25cqw.
    const jitterX = (Math.random() - 0.5) * 0.75;
    const jitterY = (Math.random() - 0.5) * 0.5;
    const x = slot.gx + jitterX;
    const y = slot.gy + jitterY;
    // Radial unit vector from the cluster centre (the parent motion
    // div sits at 50/50, so this is also the on-screen direction).
    // Cards at the centre get a small tiebreak so they don't lose
    // direction to zero.
    const mag = Math.hypot(x, y) || 1;
    const outX = (x / mag) * 1;
    const outY = (y / mag) * 1;
    // Foreground cards (depth 1.0) get slightly exaggerated rotation;
    // back-tier cards stay calmer so the layered look reads cleanly.
    const rot = (Math.random() - 0.5) * (slot.depth >= 0.95 ? 14 : 9);
    return {
      x,
      y,
      rot,
      scale: slot.depth,
      zIndex: Math.round(slot.depth * 100),
      outX,
      outY,
      // Bob amplitude in cqw. Was 2–5px, now 0.125–0.3125cqw.
      bobAmp: 0.125 + Math.random() * 0.1875,
      bobPhase: Math.random() * Math.PI * 2,
      fanDelay: Math.random() * 0.08,
    };
  });

  _sandboxSampleCache = { source: sorceryCards, picks, layout };
  return _sandboxSampleCache;
}

// Orchestrator. On hover, children fan out in a quick radial burst
// with a short stagger so the reveal has a wave quality.
const SANDBOX_FLOW_VARIANTS = {
  idle: { transition: { staggerChildren: 0 } },
  hover: { transition: { staggerChildren: 0.025, staggerDirection: 1 } },
};

// Per-card variants.
//
// Idle: holds the card at its slotted pose with a gentle bob + sway
//   driven by framer's keyframe mode so cards feel "alive" at rest.
//
// Hover: cards fan OUTWARD from the cluster centre. Each slides
//   along its own radial vector, straightens slightly, and lifts in
//   scale. This reads as "all these cards are here to play with"
//   instead of the previous unidirectional sweep-off-screen, and the
//   return animation on un-hover is symmetric so there's no
//   snap-back oddness.
const FAN_DISTANCE = 2.375;  // cqw each card pushes along its radial vector on hover (was 38px)
const FAN_SCALE = 1.08;      // slight lift so the cluster feels "lifted" on hover

// framer-motion x/y use cqw strings so the animation interpolates in
// canvas-relative space and stays visually consistent at any canvas
// size. Mixing units in keyframe arrays would break interpolation,
// so we tag every value with the same `cqw` suffix.
function sandboxCardVariants(card) {
  const bobUp = `${card.y - card.bobAmp}cqw`;
  const bobDown = `${card.y + card.bobAmp}cqw`;
  return {
    idle: {
      x: `${card.x}cqw`,
      y: [bobUp, bobDown, bobUp],
      rotate: [card.rot - 1.1, card.rot + 1.1, card.rot - 1.1],
      scale: card.scale,
      transition: {
        x: { type: 'spring', stiffness: 160, damping: 22 },
        y: { duration: 4.6 + card.bobAmp * 4, ease: 'easeInOut', repeat: Infinity, repeatType: 'mirror' },
        rotate: { duration: 5.4, ease: 'easeInOut', repeat: Infinity, repeatType: 'mirror' },
        scale: { type: 'spring', stiffness: 180, damping: 24 },
      },
    },
    hover: {
      x: `${card.x + card.outX * FAN_DISTANCE}cqw`,
      y: `${card.y + card.outY * FAN_DISTANCE}cqw`,
      // Pull each card toward upright so the art reads better when
      // spread. Never fully 0. Keeps the organic feel.
      rotate: card.rot * 0.35,
      scale: card.scale * FAN_SCALE,
      transition: {
        delay: card.fanDelay,
        type: 'spring',
        stiffness: 220,
        damping: 26,
        mass: 0.7,
      },
    },
  };
}

export function SandboxHero({ hovered, sorceryCards }) {
  const { picks, layout } = pickSandboxCards(sorceryCards);
  const state = hovered ? 'hover' : 'idle';
  const apiOrigin = getLocalApiOrigin();

  return (
    <OrnateBackdrop>
      {/* Warm workbench-light wash behind the cluster so the card art
          pops against the plate metal backdrop. Radial gradient sits
          dead-centre and fades to nothing by the edges. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(55% 65% at 50% 55%, rgba(212,168,67,0.14) 0%, rgba(212,168,67,0.05) 45%, transparent 75%)',
        }}
      />

      {/* Card cluster. Centred inside the tile, positioned in the
          SANDBOX_AREA virtual canvas so the layout is independent of
          the tile's rendered size. */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          style={{
            position: 'relative',
            width: `${SANDBOX_AREA.W}cqw`,
            height: `${SANDBOX_AREA.H}cqw`,
          }}
          initial="idle"
          animate={state}
          variants={SANDBOX_FLOW_VARIANTS}
        >
          {picks.map((card, i) => {
            const L = layout[i];
            const src = card?.printings?.[0]?.image_url;
            if (!src) return null;
            // Resolve the Vercel Blob URL through the local image
            // proxy so the tile works offline after the card-image
            // sync has run and we don't leak CDN hits on every hover.
            const resolved = src.startsWith('http')
              ? sorceryImageUrl(encodeURIComponent(card.unique_id), apiOrigin)
              : src;
            return (
              <motion.div
                key={card.unique_id || i}
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: `${SANDBOX_CARD.W}cqw`,
                  height: `${SANDBOX_CARD.H}cqw`,
                  marginLeft: `${-SANDBOX_CARD.W / 2}cqw`,
                  marginTop: `${-SANDBOX_CARD.H / 2}cqw`,
                  zIndex: L.zIndex,
                  borderRadius: 'var(--radius-md)',
                  overflow: 'hidden',
                  border: `1px solid ${GOLD} 0.35)`,
                  boxShadow: '0 8px 18px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,0,0,0.35)',
                  willChange: 'transform',
                }}
                variants={sandboxCardVariants(L)}
              >
                <img
                  src={resolved}
                  alt=""
                  draggable={false}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    userSelect: 'none',
                  }}
                  onError={(e) => {
                    // Local copy missing. Fall back to the origin URL so
                    // the tile still shows something meaningful. Cheap:
                    // one failed request per card max, until the sync
                    // script lands the local image.
                    if (e.currentTarget.src !== src) e.currentTarget.src = src;
                  }}
                />
              </motion.div>
            );
          })}
        </motion.div>
      </div>

      {/* Centre-glow accent. Brightens on hover so the fan-out reveal
          feels like the cluster is lighting up rather than drifting.
          Replaces the old directional dust streak. */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(45% 55% at 50% 52%, rgba(253,230,138,0.22) 0%, rgba(253,230,138,0.06) 45%, transparent 75%)',
          mixBlendMode: 'screen',
        }}
        initial={{ opacity: 0 }}
        animate={hovered ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
      />
    </OrnateBackdrop>
  );
}

// Hover orchestrator for the draft pack fan. Staggers from the
// center outward so the middle pack leads and the wings splay a
// beat later. Reads as the player "reaching for packs".
const DRAFT_FAN_VARIANTS = {
  idle: { transition: { staggerChildren: 0.05, staggerDirection: 1 } },
  hover: { transition: { staggerChildren: 0.06, staggerDirection: 1 } },
};

// Per-pack variants. Idle keeps the pack at its static rotation and
// translateX-off-its-fan-position; hover multiplies the rotation out,
// pushes wings further from the center, and lifts each pack upward
// (middle lifts highest like it's being offered for the first pick).
// x/y values are cqw strings so the fan-out distance scales with the
// canvas instead of staying frozen at a raw px size on large displays.
function draftPackVariants(pack, isMiddle) {
  return {
    idle: {
      x: '0cqw',
      y: '0cqw',
      rotate: pack.rot,
      scale: 1,
      transition: { type: 'spring', stiffness: 240, damping: 22 },
    },
    hover: {
      // Wings slide outward; middle doesn't shift horizontally. Was
      // ±18px / 0; now ±1.125cqw / 0cqw.
      x: isMiddle ? '0cqw' : (pack.rot > 0 ? '1.125cqw' : '-1.125cqw'),
      // Middle rises more, wings rise less. Tiered pick priority.
      // Was -14px / -6px; now -0.875cqw / -0.375cqw.
      y: isMiddle ? '-0.875cqw' : '-0.375cqw',
      // Exaggerate the fan angle so the spread reads on hover.
      rotate: pack.rot * 1.35,
      // Gentle pop on the middle pack; wings stay calm.
      scale: isMiddle ? 1.04 : 1.015,
      transition: { type: 'spring', stiffness: 260, damping: 20 },
    },
  };
}

export function DraftHero({ hovered }) {
  // Fanned trio of the real store booster packs. Each image drops
  // into a fixed-size container and uses `object-contain` + scale.
  // same pattern ArenaStore uses. So BOOSTER_SCALE can nudge
  // arthurian (whose raw art sits small in its native canvas) up to
  // match beta / gothic visually, scaled uniformly on both axes.
  // Middle pack sits forward (larger + higher z-index). Sizes are in
  // cqw (140×210 design-px ÷ 16) so the packs grow with the canvas.
  const PACK_W = 8.75;
  const PACK_H = 13.125;
  const packs = [
    { key: 'arthurian', rot: -16, leftPct: 28, fanScale: 1 },
    { key: 'beta', rot: 0, leftPct: 50, fanScale: 1.12, z: 2 },
    { key: 'gothic', rot: 16, leftPct: 72, fanScale: 1 },
  ];
  const state = hovered ? 'hover' : 'idle';
  return (
    <OrnateBackdrop>
      <motion.div
        className="absolute inset-0"
        initial="idle"
        animate={state}
        variants={DRAFT_FAN_VARIANTS}
      >
        {packs.map((p) => {
          const scale = (BOOSTER_SCALE[p.key] || 1) * p.fanScale;
          const isMiddle = p.key === 'beta';
          return (
            <motion.div
              key={p.key}
              style={{
                position: 'absolute',
                left: `${p.leftPct}%`,
                top: '50%',
                width: `${PACK_W}cqw`,
                height: `${PACK_H}cqw`,
                // `translate(-50%, -50%)` anchors the wrapper on the
                // fan point; framer-motion's x/y/rotate then drive the
                // hover motion on top of that baseline transform.
                marginLeft: `${-PACK_W / 2}cqw`,
                marginTop: `${-PACK_H / 2}cqw`,
                transformOrigin: 'center center',
                zIndex: p.z || 1,
                filter: 'drop-shadow(0 10px 24px rgba(0,0,0,0.7)) drop-shadow(0 0 14px rgba(212,168,67,0.18))',
                willChange: 'transform',
              }}
              variants={draftPackVariants(p, isMiddle)}
            >
              <img
                src={getBoosterImage(p.key)}
                alt=""
                draggable={false}
                className="max-w-full max-h-full object-contain"
                style={{
                  width: '100%',
                  height: '100%',
                  transform: `scale(${scale})`,
                  transformOrigin: 'center center',
                }}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </motion.div>
          );
        })}
      </motion.div>
    </OrnateBackdrop>
  );
}

// ---------------------------------------------------------------------------
// Sealed hero. Skeleton deck-builder UI
// ---------------------------------------------------------------------------
//
// Visual thesis: what you see when you enter Sealed mode is the deck
// builder. Mini-wireframe of that screen. Header strip with title +
// submit button, pool grid of card placeholders, right-side deck
// sidebar. Reads as "open packs, build a deck, play" at a glance.
// Pure CSS + framer-motion skeleton, no image compositing.

// Orchestrator variants for the whole UI. On hover the skeleton
// lifts slightly and children brighten in a coordinated wave.
const SEALED_UI_VARIANTS = {
  idle: {
    y: '0cqw',
    scale: 1,
    transition: { type: 'spring', stiffness: 240, damping: 24, staggerChildren: 0 },
  },
  hover: {
    y: '-0.25cqw',
    scale: 1.02,
    transition: { type: 'spring', stiffness: 260, damping: 22, staggerChildren: 0.03 },
  },
};

// Per-pool-card variants. Each skeleton tile brightens + lifts a
// touch on hover, staggered so the grid "populates" like the builder
// is filling cards in as you pick them.
const SEALED_TILE_VARIANTS = {
  idle: {
    opacity: 0.82,
    y: '0cqw',
    borderColor: 'rgba(180,140,60,0.22)',
    transition: { type: 'spring', stiffness: 220, damping: 24 },
  },
  hover: {
    opacity: 1,
    y: '-0.125cqw',
    borderColor: 'rgba(212,168,67,0.55)',
    transition: { type: 'spring', stiffness: 260, damping: 22 },
  },
};

// Shimmer sweep that crosses the whole mini-UI on hover. Same
// technique the old pool image used, preserved because it reads as
// "the UI is coming alive" rather than "the cards are moving".
const SEALED_SHIMMER_VARIANTS = {
  idle: { x: '-120%', opacity: 0, transition: { duration: 0 } },
  hover: {
    x: ['-120%', '120%'],
    opacity: [0, 0.45, 0.45, 0],
    transition: { duration: 1.05, times: [0, 0.1, 0.9, 1], ease: 'easeOut' },
  },
};

// Deck sidebar row. A horizontal placeholder bar. Each row's width
// is stable so the sidebar reads as a real deck list, not a bunch of
// identical bars.
const SEALED_SIDEBAR_ROWS = [
  { w: '78%' }, { w: '64%' }, { w: '72%' }, { w: '58%' },
  { w: '68%' }, { w: '62%' }, { w: '54%' },
];

// Pool grid dimensions. Tight enough to read as a grid at the tile's
// miniature scale, loose enough that each cell is still card-shaped.
const SEALED_POOL_COLS = 6;
const SEALED_POOL_ROWS = 3;

export function SealedHero({ hovered }) {
  const state = hovered ? 'hover' : 'idle';
  return (
    <OrnateBackdrop accentColor={TEXT_MUTED}>
      {/* The whole wireframe. Scaled down to sit inside the tile
          with comfortable breathing room. Uses motion for the unified
          lift + stagger on hover. `inset-x` + padding gives a plate
          look, as if the UI is mounted into the tile frame. */}
      <motion.div
        className="absolute flex flex-col"
        style={{
          left: '6%',
          right: '6%',
          top: '10%',
          bottom: '10%',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          background: 'linear-gradient(180deg, rgba(20,16,10,0.75) 0%, rgba(12,10,8,0.9) 100%)',
          border: `1px solid ${GOLD} 0.18)`,
          boxShadow: '0 8px 24px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,220,140,0.06)',
          willChange: 'transform',
        }}
        initial="idle"
        animate={state}
        variants={SEALED_UI_VARIANTS}
      >
        {/* Header. Mirrors the real "Build Your Deck" bar */}
        <div
          className="flex items-center gap-2 px-2 shrink-0"
          style={{
            height: '1.125cqw',
            background: 'rgba(12,10,8,0.85)',
            borderBottom: `1px solid ${GOLD} 0.15)`,
          }}
        >
          {/* Title placeholder */}
          <div
            style={{
              width: '3.25cqw',
              height: '0.3125cqw',
              borderRadius: 'var(--radius-xs)',
              background: `linear-gradient(90deg, ${ACCENT_GOLD} 0%, rgba(212,168,67,0.3) 100%)`,
            }}
          />
          {/* Subtitle */}
          <div
            style={{ width: '1.75cqw', height: '0.1875cqw', borderRadius: 'var(--radius-xs)', background: `${GOLD} 0.22)` }}
          />
          {/* Timer placeholder */}
          <div
            className="ml-auto flex items-center gap-1.5"
          >
            <div
              style={{
                width: '1.375cqw',
                height: '0.5cqw',
                borderRadius: 'var(--radius-xs)',
                background: 'rgba(20,16,10,0.85)',
                border: `1px solid ${GOLD} 0.3)`,
              }}
            />
            {/* Submit button */}
            <div
              style={{
                width: '2.125cqw',
                height: '0.625cqw',
                borderRadius: 'var(--radius-xs)',
                background: `linear-gradient(180deg, rgba(212,168,67,0.85) 0%, rgba(160,120,40,0.85) 100%)`,
                boxShadow: 'inset 0 1px 0 rgba(255,220,140,0.35), 0 1px 2px rgba(0,0,0,0.5)',
              }}
            />
          </div>
        </div>

        {/* Sub-header. Avatar + basic sites selector row */}
        <div
          className="flex items-center gap-2 px-2 shrink-0"
          style={{
            height: '0.875cqw',
            background: 'rgba(8,6,4,0.7)',
            borderBottom: `1px solid ${GOLD} 0.1)`,
          }}
        >
          {/* "Avatar" label + circle */}
          <div style={{ width: '1.125cqw', height: '0.15625cqw', borderRadius: 'var(--radius-xs)', background: `${GOLD} 0.3)` }} />
          <div
            style={{
              width: '0.5625cqw',
              height: '0.5625cqw',
              borderRadius: '50%',
              background: `radial-gradient(circle at 35% 30%, rgba(255,220,140,0.35), ${GOLD} 0.5) 70%)`,
              border: `1px solid ${GOLD} 0.45)`,
            }}
          />
          {/* "Basic Sites" label + element pips */}
          <div style={{ width: '1.375cqw', height: '0.15625cqw', borderRadius: 'var(--radius-xs)', background: `${GOLD} 0.3)`, marginLeft: '0.375cqw' }} />
          <div className="flex gap-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                style={{
                  width: '0.4375cqw',
                  height: '0.4375cqw',
                  borderRadius: '50%',
                  background: `${GOLD} 0.14)`,
                  border: `1px solid ${GOLD} 0.3)`,
                }}
              />
            ))}
          </div>
        </div>

        {/* Body. Pool grid + deck sidebar */}
        <div className="flex flex-1 min-h-0" style={{ gap: '0.25cqw', padding: '0.25cqw 0.1875cqw 0.1875cqw 0.25cqw' }}>
          {/* Pool grid (left) */}
          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
            {/* "Pool" label */}
            <div style={{ width: '1.125cqw', height: '0.125cqw', borderRadius: 'var(--radius-xs)', background: `${GOLD} 0.3)` }} />
            {/* Grid of skeleton cards */}
            <div
              className="flex-1"
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${SEALED_POOL_COLS}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${SEALED_POOL_ROWS}, minmax(0, 1fr))`,
                gap: '0.15625cqw',
              }}
            >
              {Array.from({ length: SEALED_POOL_COLS * SEALED_POOL_ROWS }).map((_, i) => (
                <motion.div
                  key={i}
                  style={{
                    borderRadius: 'var(--radius-xs)',
                    background: `linear-gradient(155deg, rgba(180,140,60,0.2) 0%, rgba(25,18,10,0.9) 100%)`,
                    border: `1px solid`,
                    // Slight foiling shimmer on every 5th tile, rotating
                    // through the grid so the composition doesn't read as
                    // uniform. Matches the "rare card" cadence of the
                    // real pool.
                    boxShadow: i % 5 === 2
                      ? 'inset 0 0 0 1px rgba(255,220,140,0.22), 0 2px 4px rgba(0,0,0,0.5)'
                      : '0 2px 4px rgba(0,0,0,0.45)',
                    willChange: 'transform, opacity, border-color',
                  }}
                  variants={SEALED_TILE_VARIANTS}
                />
              ))}
            </div>
          </div>

          {/* Deck sidebar (right). Fixed narrow column */}
          <div
            className="shrink-0 flex flex-col gap-1"
            style={{
              width: '3.625cqw',
              padding: '0.1875cqw 0.25cqw',
              borderRadius: 'var(--radius-xs)',
              background: 'rgba(14,10,6,0.75)',
              border: `1px solid ${GOLD} 0.16)`,
            }}
          >
            {/* Sidebar header label */}
            <div style={{ width: '70%', height: '0.15625cqw', borderRadius: 'var(--radius-xs)', background: `${GOLD} 0.45)` }} />
            {/* Card entries */}
            <div className="flex flex-col gap-1 flex-1">
              {SEALED_SIDEBAR_ROWS.map((row, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1"
                  style={{
                    padding: '0.0625cqw 0.125cqw',
                    borderRadius: 'var(--radius-xs)',
                    background: i === 0 ? `${GOLD} 0.08)` : 'transparent',
                  }}
                >
                  {/* Quantity pill */}
                  <div
                    style={{
                      width: '0.375cqw',
                      height: '0.25cqw',
                      borderRadius: 'var(--radius-xs)',
                      background: i % 3 === 0 ? `${GOLD} 0.4)` : `${GOLD} 0.25)`,
                    }}
                  />
                  {/* Name bar */}
                  <div
                    style={{
                      height: '0.1875cqw',
                      borderRadius: 'var(--radius-xs)',
                      flex: 1,
                      background: `linear-gradient(90deg, ${GOLD} 0.35) 0%, ${GOLD} 0.12) ${row.w}, rgba(0,0,0,0) ${row.w})`,
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Gold shimmer sweep. Crosses the whole mini-UI on hover,
            clipped by the parent's overflow-hidden. */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `linear-gradient(
              100deg,
              transparent 35%,
              rgba(255,240,180,0.45) 48%,
              rgba(255,240,180,0.75) 50%,
              rgba(255,240,180,0.45) 52%,
              transparent 65%
            )`,
            mixBlendMode: 'screen',
            willChange: 'transform, opacity',
          }}
          initial="idle"
          animate={state}
          variants={SEALED_SHIMMER_VARIANTS}
        />
      </motion.div>
    </OrnateBackdrop>
  );
}

export const HERO_BY_KEY = {
  // Legacy keys kept for any call site still on the old mode
  // registry during cleanup.
  ranked: RankedHero,
  'friend-match': FriendMatchHero,
  casual: SoloHero,
  // Instance-era keys (modes). 'match' reuses the friend-match
  // hero art per the play-screen redesign. The visual meaning
  // of 'match' in the instance model is closer to a direct 1v1
  // (ranked is a per-match toggle, not a mode) so FriendMatchHero
  // reads more accurately than the legacy RankedHero.
  match: FriendMatchHero,
  sandbox: SandboxHero,
  draft: DraftHero,
  sealed: SealedHero,
  // Instance-era keys (visibilities, reused in SessionBrowser's
  // visibility strip via ModeTile).
  solo: SoloHero,
  friends: FriendMatchHero,
  public: RankedHero,
};
